import { DOKUMENTE, type Dokumentart } from '../kapitel';
import {
    abschnittVon,
    istBestaetigt,
    kapitelDesDokuments,
    regelnDesKapitels,
    sortiereLuecken,
    sortiereRollen,
} from '../bauordnungLogic';
import { loeseKopfPlatzhalter, tagDeutsch } from '../bausteine/automatik';
import type { KopfFeld } from '../bausteine/platzhalter';
import type { Bauordnung, BauordnungBundle } from '../types';
import type { Dokument, Knoten } from './dokumentbeschreibung';
import type { ProjektMerkmale } from '../../planungsphase/types';

/**
 * Baut die Beschreibung EINES Dokuments aus EINEM Datenbestand.
 *
 * Eine Funktion fuer beide Ausgaben, nicht zwei: was ein Dokument fuehrt,
 * steht in seiner Kapitelliste (`DOKUMENTE`), nicht in einer zweiten
 * Bau-Funktion. Zwei Funktionen liefen auseinander, sobald jemand nur eine
 * anfasst — und der Unterschied faellt erst im gedruckten Dokument auf.
 *
 * KOPF-PLATZHALTER: Regelungen und Einleitungen tragen `{revision_nr}`,
 * `{geltung_ende}` & Co. bis hierher unaufgeloest (`bausteine/platzhalter.ts`).
 * Erst hier werden sie aus dem Kopfsatz gelesen — aus DEMSELBEN, aus dem
 * auch das Deckblatt seine Zeilen „Stand", „Fassung", „Geltungsdauer" nimmt.
 * Text und Deckblatt koennen so nicht auseinanderlaufen; nach der Freigabe
 * ist das der freigegebene Kopfsatz mit der Fassung aus dem Freigabefeld.
 *
 * Diese Datei importiert NICHTS aus `jspdf`.
 */

/** Ohne Kopfsatz loest jeder Kopf-Platzhalter zu `[Kurz]` auf — nie zu einem rohen `{name}` im Druck. */
const OHNE_KOPF: Pick<Bauordnung, KopfFeld> = {
    erlassender_firma: '', erlassender_name: '', stand: null, version_label: null,
    geltung_von: null, geltung_bis: null, geltung_ort: '',
};

export interface BauKontext {
    projektName?: string;
    projektNummer?: string;
    projektAdresse?: string;
    merkmale: ProjektMerkmale;
}

const ROLLEN_LABEL: Record<string, string> = {
    keine: 'keine Weisungsbefugnis',
    begrenzt_dguv1_6: 'begrenzt (§ 6 DGUV Vorschrift 1)',
    bauleitung_lbo: 'Bauleitung nach LBO',
    arbeitgeber: 'Arbeitgeber gegenüber eigenen Beschäftigten',
};

const ERLASSENDER_LABEL: Record<string, string> = {
    bauherr: 'Bauherr',
    gu: 'Generalunternehmer',
    bauleitung: 'Bauleitung',
};

export const baueDokument = (
    bundle: BauordnungBundle,
    dokument: Dokumentart,
    kontext: BauKontext,
): Dokument => {
    const def = DOKUMENTE[dokument];
    const o = bundle.ordnung;
    const knoten: Knoten[] = [];
    /** Gespeicherter Text → gedruckter Text: Kopf-Platzhalter aus dem Kopfsatz, Rand ab. */
    const imDruck = (text: string): string => loeseKopfPlatzhalter(text, o ?? OHNE_KOPF).trim();

    const gezogen = kapitelDesDokuments(bundle, dokument, kontext.merkmale);
    const offeneLuecken = sortiereLuecken(bundle.luecken).filter(l => !l.erledigt_am);

    // ─── Deckblatt ───────────────────────────────────────────────────────
    const deckblatt: { merkmal: string; angabe: string }[] = [];
    if (kontext.projektName) deckblatt.push({ merkmal: 'Bauvorhaben', angabe: kontext.projektName });
    if (kontext.projektNummer) deckblatt.push({ merkmal: 'Projektnummer', angabe: kontext.projektNummer });
    if (kontext.projektAdresse) deckblatt.push({ merkmal: 'Baustelle', angabe: kontext.projektAdresse });
    if (o?.erlassender_firma) {
        // Der Erlassende ist HERAUSGEBER. Das verfassende Buero erscheint
        // ausschliesslich klein in der Fusszeile — wer nicht erlaesst,
        // unterschreibt auch nicht.
        deckblatt.push({
            merkmal: 'Erlassen durch',
            angabe: [o.erlassender_firma, o.erlassender_rolle ? `(${ERLASSENDER_LABEL[o.erlassender_rolle]})` : '']
                .filter(Boolean).join(' '),
        });
    }
    if (o?.geltung_ort) deckblatt.push({ merkmal: 'Geltungsbereich', angabe: o.geltung_ort });
    // Datum wie in den Regeltexten (TT.MM.JJJJ, `loeseKopfPlatzhalter`) — ein
    // ISO-Datum auf dem Deckblatt neben „23.08.2026" im Text fiele zuerst auf.
    if (o?.geltung_von || o?.geltung_bis) {
        deckblatt.push({
            merkmal: 'Geltungsdauer',
            angabe: `${tagDeutsch(o?.geltung_von) || o?.geltung_von || 'offen'} bis ${tagDeutsch(o?.geltung_bis) || o?.geltung_bis || 'offen'}`,
        });
    }
    if (o?.stand) deckblatt.push({ merkmal: 'Stand', angabe: tagDeutsch(o.stand) || o.stand });
    if (o?.version_label) deckblatt.push({ merkmal: 'Fassung', angabe: o.version_label });
    deckblatt.push({
        merkmal: 'Status',
        angabe: o?.status === 'freigegeben'
            ? `freigegeben${o.freigegeben_am ? ` am ${o.freigegeben_am.slice(0, 10)}` : ''}`
            : 'Entwurf',
    });

    // ─── Der Grundsatz steht im Kopf jeder Ausgabe ───────────────────────
    knoten.push({
        art: 'hinweis',
        text: 'Dieses Dokument ergänzt den SiGe-Plan, es ersetzt ihn nicht. Es ersetzt weder die '
            + 'Gefährdungsbeurteilung nach § 5 ArbSchG noch die Unterweisung nach § 12 ArbSchG; '
            + 'beide bleiben Aufgabe des jeweiligen Arbeitgebers für seine eigenen Beschäftigten.',
    });

    let letzterTeil = '';
    for (const g of gezogen) {
        const kennung = g.definition.id;

        // Teilüberschrift, sobald ein neuer Teil beginnt (HSE-Plan).
        if (g.teil.id !== letzterTeil) {
            letzterTeil = g.teil.id;
            if (def.teile.length > 1 && g.teil.nummerierung !== 'ohne') {
                knoten.push({ art: 'seitenumbruch' });
                knoten.push({ art: 'ueberschrift', ebene: 1, text: g.teil.titel });
                if (g.teil.hinweis) knoten.push({ art: 'absatz', text: g.teil.hinweis, betont: true });
            }
        }

        const abschnitt = abschnittVon(bundle.abschnitte, kennung);

        // Ein ausdrücklich ausgeschlossenes Kapitel wird NICHT still
        // weggelassen: „geprüft, trifft nicht zu" sagt etwas anderes als ein
        // fehlendes Kapitel.
        if (abschnitt && !abschnitt.gezogen) {
            knoten.push({ art: 'ueberschrift', ebene: 2, nummer: g.nummer, text: g.definition.titel, kapitel: kennung });
            knoten.push({
                art: 'leerbefund',
                text: abschnitt.bemerkung.trim()
                    ? `Geprüft, trifft für dieses Bauvorhaben nicht zu. ${abschnitt.bemerkung.trim()}`
                    : 'Geprüft, trifft für dieses Bauvorhaben nicht zu.',
            });
            continue;
        }

        knoten.push({
            art: 'ueberschrift',
            ebene: g.teil.nummerierung === 'ohne' ? 1 : 2,
            nummer: g.nummer,
            text: g.definition.titel,
            kapitel: kennung,
        });

        if (abschnitt?.einleitung.trim()) {
            knoten.push({ art: 'absatz', text: imDruck(abschnitt.einleitung) });
        }

        // ─── Die Sonderkapitel, die sich aus dem Bestand selbst füllen ───
        if (kennung === 'offene_punkte') {
            if (!offeneLuecken.length) {
                knoten.push({ art: 'absatz', text: 'Zum Stand dieses Dokuments bestehen keine offenen Punkte.' });
            } else {
                knoten.push({
                    art: 'tabelle',
                    kopf: ['Nr.', 'Offener Punkt', 'Verantwortlich', 'Frist'],
                    zeilen: offeneLuecken.map(l => [
                        String(l.nr), l.punkt, l.verantwortlich || '—', l.frist || 'offen',
                    ]),
                });
            }
            continue;
        }

        if (kennung === 'organisation' || kennung === 'unterschrift') {
            const rollen = sortiereRollen(bundle.rollen);
            if (kennung === 'organisation') {
                if (!rollen.length) {
                    knoten.push({ art: 'leerbefund', text: 'Die Projektorganisation ist noch nicht erfasst.' });
                } else {
                    knoten.push({
                        art: 'tabelle',
                        kopf: ['Rolle', 'Name', 'Firma', 'Erreichbarkeit', 'Weisungsbefugnis'],
                        zeilen: rollen.map(r => [
                            r.rolle || r.funktion || '—', r.name || '—', r.firma || '—',
                            r.erreichbarkeit || '—',
                            ROLLEN_LABEL[r.weisungsbefugnis] + (r.befugnis_umfang ? ` — ${r.befugnis_umfang}` : ''),
                        ]),
                    });
                }
            }
        }

        // ─── Die erfassten Regelungen ────────────────────────────────────
        const regeln = regelnDesKapitels(bundle.regeln, kennung).filter(istBestaetigt);
        if (regeln.length) {
            knoten.push({ art: 'liste', punkte: regeln.map(r => imDruck(r.wortlaut)) });

            const mitVerantwortung = regeln.filter(r => r.verantwortlich.trim() || r.rhythmus.trim());
            if (mitVerantwortung.length) {
                knoten.push({
                    art: 'tabelle',
                    kopf: ['Regelung', 'Verantwortlich', 'Rhythmus'],
                    zeilen: mitVerantwortung.map(r => [
                        imDruck(r.wortlaut).slice(0, 120), r.verantwortlich || '—', r.rhythmus || '—',
                    ]),
                });
            }
        } else if (kennung !== 'deckblatt' && kennung !== 'revisionsverzeichnis' && kennung !== 'kenntnisnahme') {
            // Leer heisst leer — und wird als solches gedruckt. Ein
            // stillschweigend uebersprungenes Pflichtkapitel behauptet, es
            // gebe dazu nichts zu regeln.
            knoten.push({
                art: 'leerbefund',
                text: g.pflicht
                    ? 'Zu diesem Kapitel ist noch keine Regelung erfasst. Vor Baubeginn zu ergänzen.'
                    : 'Zu diesem Kapitel ist keine Regelung erfasst.',
            });
        }

        // Lücken, die genau an diesem Kapitel hängen, stehen an der Textstelle
        // UND weiter unten in der Sammelliste.
        for (const l of offeneLuecken.filter(x => x.kapitel === kennung)) {
            knoten.push({ art: 'luecke', nr: l.nr, text: l.punkt });
        }

        // ─── Kenntnisnahme und Unterschrift ──────────────────────────────
        if (kennung === 'kenntnisnahme' || kennung === 'unterschrift') {
            knoten.push({
                art: 'absatz',
                text: 'Mit der Unterschrift wird bestätigt, dass die vorstehenden Regelungen gelesen, '
                    + 'verstanden und an die eigenen Beschäftigten sowie an alle Nachunternehmer '
                    + 'weitergegeben werden.',
            });
            knoten.push({
                art: 'unterschriftsfeld',
                zeilen: [
                    {
                        rolle: o?.erlassender_funktion || 'Erlassender',
                        name: o?.erlassender_name || '',
                        firma: o?.erlassender_firma || '',
                    },
                    ...(kennung === 'kenntnisnahme'
                        ? [{ rolle: 'Auftragnehmer (Gegenzeichnung)', name: '', firma: '' }]
                        : []),
                ],
            });
        }
    }

    return {
        titel: def.titel,
        untertitel: def.untertitel,
        deckblatt,
        verfasserZeile: o?.verfasser_zeile || '',
        entwurf: o?.status !== 'freigegeben',
        knoten,
    };
};

/**
 * Dateiname der Ausgabe.
 *
 * Ohne Schraegstriche und ohne Leerzeichen: der Name landet auch als
 * Objektname im Storage, und dort ist ein Schraegstrich ein neuer Ordner.
 * Umlaute bleiben ausdruecklich stehen — sie sind in beiden Ausgabeformaten
 * unkritisch und in einem deutschen Dateinamen erwartbar.
 */
export const bauordnungDateiname = (
    dokument: Dokumentart,
    projektName: string,
    stand?: string | null,
    endung: 'pdf' | 'docx' = 'pdf',
): string => {
    const sauber = (s: string) => (s || '')
        .replace(/[^\wÄÖÜäöüß\- ]+/g, '')
        .trim()
        .replace(/\s+/g, '_')
        .slice(0, 60);
    const teile = [
        dokument === 'hse' ? 'HSE-Plan' : 'Baustellenordnung',
        sauber(projektName),
        (stand || '').slice(0, 10) || 'ohne-Datum',
    ];
    return `${teile.filter(Boolean).join('_')}.${endung}`;
};
